/* oxlint-disable no-inner-declarations */
import { ok } from '@oazapfts/runtime';
import * as rq from '@tanstack/react-query';
import { Err, Ok, Result } from 'ts-results-es';

import type { TaskDetails } from '@/task-manager/entities/task-details';

import { type BodyOf, tasksV1CardsFilesUploadCreate } from '../fetch-client';
import { ListTaskFiles } from '../queries/list-task-files';
import { useFetchOpts } from '../use-fetch-opts';

export namespace CreateTaskFiles {
  export const ALLOWED_FILE_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
    'image/jpeg',
    'image/png',
    'image/gif',
    'text/plain',
  ];

  export const MAX_FILE_SIZE_MB = 100;
  export const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

  export type Input = { task: TaskDetails; files: File[] };
  export type Output = void;
  export type Error =
    | {
        type: 'FILE_TOO_BIG';
        filename: string;
        limit: number;
      }
    | {
        type: 'FILE_INVALID_TYPE';
        given: string;
      };

  function bodyFromInput(
    input: Input,
  ): BodyOf<typeof tasksV1CardsFilesUploadCreate> {
    return {
      uuid: input.task.id,
      fileUploadRequest: { files: input.files },
    };
  }

  export function useMutation() {
    const getOpts = useFetchOpts();
    const queryClient = rq.useQueryClient();

    return rq.useMutation<Result<Output, Error>, unknown, Input>({
      async mutationFn(input) {
        for (const file of input.files) {
          if (file.size > MAX_FILE_SIZE) {
            return Err({
              type: 'FILE_TOO_BIG',
              filename: file.name,
              limit: MAX_FILE_SIZE,
            });
          }
          if (!ALLOWED_FILE_TYPES.includes(file.type)) {
            return Err({
              type: 'FILE_INVALID_TYPE',
              given: file.type,
            });
          }
        }

        const opts = await getOpts();
        const body = bodyFromInput(input);
        await ok(tasksV1CardsFilesUploadCreate(body, opts));
        return Ok(undefined);
      },
      async onSuccess(result, { task }) {
        if (result.isOk()) {
          await queryClient.invalidateQueries({
            queryKey: ListTaskFiles.key(task),
          });
        }
      },
    });
  }
}
